import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchWhatsappSettings,
  updateWhatsappSettings,
  fetchInstitutionWallets,
  linkDevice,
  topUpTokens,
} from "../service/whatsappService";
import type { WhatsappSettings, InstitutionWallet } from "../types";

export const loadWhatsappSettings = createAsyncThunk("whatsapp/loadSettings", async () => fetchWhatsappSettings());

export const saveWhatsappSettings = createAsyncThunk(
  "whatsapp/saveSettings",
  async (payload: { baseUrl?: string; embedBaseUrl?: string; mobileAppUrl?: string; webLoginUrl?: string; sendingPaused?: boolean }) => updateWhatsappSettings(payload)
);

export const loadInstitutionWallets = createAsyncThunk(
  "whatsapp/loadWallets",
  async (params: { page: number; limit: number; search?: string }) => fetchInstitutionWallets(params)
);

export const linkInstitutionDevice = createAsyncThunk(
  "whatsapp/linkDevice",
  async ({ institutionId, apiKey, virtualNumberId }: { institutionId: number; apiKey: string; virtualNumberId: string }) =>
    linkDevice(institutionId, apiKey, virtualNumberId)
);

export const topUpInstitutionTokens = createAsyncThunk(
  "whatsapp/topUp",
  async ({ institutionId, amount }: { institutionId: number; amount: number }) => topUpTokens(institutionId, amount)
);

interface WhatsappState {
  settings: WhatsappSettings | null;
  wallets: InstitutionWallet[];
  walletsPagination: { page: number; limit: number; total: number; totalPages: number };
  settingsLoading: boolean;
  walletsLoading: boolean;
  error: string | null;
}

const initialState: WhatsappState = {
  settings: null,
  wallets: [],
  walletsPagination: { page: 1, limit: 20, total: 0, totalPages: 1 },
  settingsLoading: false,
  walletsLoading: false,
  error: null,
};

function upsertWallet(state: WhatsappState, wallet: InstitutionWallet) {
  const index = state.wallets.findIndex((w) => w.institutionId === wallet.institutionId);
  if (index === -1) state.wallets.push(wallet);
  else state.wallets[index] = wallet;
}

const whatsappSlice = createSlice({
  name: "whatsapp",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadWhatsappSettings.pending, (state) => {
        state.settingsLoading = true;
      })
      .addCase(loadWhatsappSettings.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.settings = action.payload;
      })
      .addCase(loadWhatsappSettings.rejected, (state, action) => {
        state.settingsLoading = false;
        state.error = action.error.message ?? "Failed to load WhatsApp settings";
      })
      .addCase(saveWhatsappSettings.fulfilled, (state, action) => {
        state.settings = action.payload;
      })
      .addCase(loadInstitutionWallets.pending, (state) => {
        state.walletsLoading = true;
      })
      .addCase(loadInstitutionWallets.fulfilled, (state, action) => {
        state.walletsLoading = false;
        state.wallets = action.payload.institutionWallets;
        state.walletsPagination = action.payload.pagination;
      })
      .addCase(loadInstitutionWallets.rejected, (state, action) => {
        state.walletsLoading = false;
        state.error = action.error.message ?? "Failed to load institution wallets";
      })
      .addCase(linkInstitutionDevice.fulfilled, (state, action) => {
        upsertWallet(state, action.payload);
      })
      .addCase(topUpInstitutionTokens.fulfilled, (state, action) => {
        upsertWallet(state, action.payload);
      });
  },
});

export default whatsappSlice.reducer;
